import { useParams, Link, Navigate } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLayout from '../components/PageLayout';
import LeadForm from '../components/LeadForm';
import { SERVICE_CONTENT } from '../lib/service-content';
import { SERVICES, WHATSAPP_LINK } from '../lib/site';
import { ArrowRight, Check, PhoneCall } from 'lucide-react';
import { useState } from 'react';

/**
 * Página de servicio — template único para las 5 páginas de servicio.
 * Contenido SEO (800+ palabras, E-E-A-T) en lib/service-content.ts.
 */
const ServicioPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const content = slug ? SERVICE_CONTENT[slug] : undefined;
  const [faqOpen, setFaqOpen] = useState<number | null>(0);

  if (!content) return <Navigate to="/404" replace />;

  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <PageLayout
      label={content.title}
      title={content.heroLine.split(' ').slice(0, -1).join(' ')}
      titleEm={content.heroLine.split(' ').slice(-1)[0]}
      lead={content.intro[0].slice(0, content.intro[0].lastIndexOf(' ', 160)) + '…'}
      crumbs={[{ name: 'Servicios', href: '/#servicios' }, { name: content.title }]}
    >
      <SEO
        title={content.metaTitle}
        description={content.metaDescription}
        url={`/servicios/${slug}`}
        image={content.image}
        breadcrumbs={[
          { name: 'Inicio', url: '/' },
          { name: 'Servicios', url: '/#servicios' },
          { name: content.title, url: `/servicios/${slug}` },
        ]}
        serviceSchema={{
          name: content.title,
          description: content.metaDescription,
          areaServed: ['Málaga', 'Marbella', 'Torremolinos', 'Benalmádena', 'Fuengirola'],
        }}
        faqSchema={content.faqs}
      />

      {/* Intro + imagen */}
      <section className="max-w-[1400px] mx-auto px-6 py-16 md:py-20">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-start">
          <div className="space-y-5 text-ink-soft leading-relaxed reveal">
            {content.intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/presupuesto" className="btn bg-clay text-white">
                Presupuesto gratis en 24h
                <ArrowRight size={16} />
              </Link>
              <a href={`tel:+34643670832`} className="btn border border-ink text-ink">
                <PhoneCall size={16} />
                +34 643 67 08 32
              </a>
            </div>
          </div>

          <figure className="reveal">
            <div className="rounded-[4px] overflow-hidden bg-paper-2 shadow-paper">
              <img
                src={content.image}
                alt={content.imageAlt}
                className="w-full object-cover aspect-[4/5] max-h-[560px]"
                loading="lazy"
              />
            </div>
            <figcaption className="mt-3 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-ink-faint">
              {content.title} · HGO Servicios
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Características */}
      <section className="bg-paper-2 border-y border-line py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="reveal font-display font-black uppercase tracking-tight text-2xl md:text-3xl">
            Por qué <em className="font-serifit normal-case font-normal text-clay">HGO</em>
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
            {content.features.map((f, i) => (
              <div key={i} className="reveal bg-paper p-7" style={{ transitionDelay: `${i * 60}ms` }}>
                <span className="grid place-items-center w-8 h-8 rounded-full bg-clay text-white mb-5">
                  <Check size={15} strokeWidth={3} />
                </span>
                <h3 className="font-display font-bold tracking-tight uppercase text-sm">{f.title}</h3>
                <p className="mt-2.5 text-sm text-ink-soft leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Secciones de contenido SEO */}
      <section className="max-w-[1400px] mx-auto px-6 py-16 md:py-24">
        <div className="max-w-3xl space-y-14">
          {content.sections.map((sec, i) => (
            <div key={i} className="reveal">
              <span className="section-index">0{i + 2}</span>
              <h2 className="mt-3 font-display font-black uppercase tracking-[-0.015em] text-2xl md:text-[1.9rem] leading-tight">
                {sec.heading}
              </h2>
              <div className="mt-5 space-y-4 text-ink-soft leading-relaxed">
                {sec.body.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ de servicio */}
      <section className="bg-paper-2 border-y border-line py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-[0.8fr_1.4fr] gap-12 lg:gap-20">
          <div className="reveal lg:sticky lg:top-28 self-start">
            <h2 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl">
              Dudas sobre {content.title.toLowerCase()}
            </h2>
            <p className="mt-4 text-ink-soft leading-relaxed text-sm max-w-sm">
              Respuestas directas a lo que más preguntan nuestros clientes antes de empezar.
            </p>
          </div>
          <div className="reveal">
            {content.faqs.map((f, i) => (
              <div key={i} className="faq-item" data-open={faqOpen === i}>
                <button className="faq-question" onClick={() => setFaqOpen(faqOpen === i ? null : i)} aria-expanded={faqOpen === i}>
                  <span>{f.q}</span>
                  <span className="faq-icon" aria-hidden="true">+</span>
                </button>
                <div className="faq-answer">
                  <div className="faq-answer-inner">
                    <p>{f.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Otros servicios */}
      <section className="max-w-[1400px] mx-auto px-6 py-16 md:py-20">
        <h2 className="reveal font-display font-black uppercase tracking-tight text-2xl">
          También <em className="font-serifit normal-case font-normal text-clay">hacemos</em>
        </h2>
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {related.map((s) => (
            <Link
              key={s.slug}
              to={`/servicios/${s.slug}`}
              className="reveal group flex items-center justify-between gap-4 bg-paper-2 border border-line rounded-[4px] p-6 hover:border-clay/60 transition-colors duration-200"
            >
              <div>
                <p className="section-index">{s.index}</p>
                <h3 className="mt-1.5 font-display font-bold tracking-tight uppercase text-base group-hover:text-clay transition-colors duration-200">
                  {s.title}
                </h3>
              </div>
              <ArrowRight size={18} className="text-ink-faint group-hover:text-clay group-hover:translate-x-1 transition-all duration-200" />
            </Link>
          ))}
        </div>

        {/* Captación in-page */}
        <div className="mt-14 grid lg:grid-cols-[1fr_0.85fr] gap-10 items-center reveal">
          <div>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl leading-tight">
              Empieza tu {content.title.toLowerCase()} <em className="font-serifit normal-case font-normal text-clay">hoy</em>
            </h2>
            <p className="mt-4 text-ink-soft leading-relaxed max-w-md">
              Visita y medición gratuitas. Presupuesto cerrado en 24–48h con cada partida
              desglosada y plazos por escrito.
            </p>
            <a
              href={WHATSAPP_LINK(`Hola, quiero un presupuesto para ${content.title.toLowerCase()}.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn mt-7 border border-ink text-ink"
            >
              Hablar con el equipo
            </a>
          </div>
          <LeadForm compact title="Presupuesto gratis" subtitle="Te respondemos en menos de 24 horas laborables." />
        </div>
      </section>
    </PageLayout>
  );
};

export default ServicioPage;
